import React from 'react';
import { Link } from 'react-router-dom';
import { Icon } from './icons';

interface RouteErrorBoundaryProps {
  children: React.ReactNode;
}

interface RouteErrorBoundaryState {
  error: Error | null;
}

/**
 * Faengt Render-Fehler einer Seite ab, damit ein einzelner Ausfall nicht die
 * ganze App auf eine weisse Seite wirft. Die Seitenleiste bleibt stehen, der
 * Nutzer sieht eine lesbare Meldung und kann weiternavigieren. Beim Wechsel
 * der Route wird die Schranke per `key={pathname}` neu gemountet (siehe
 * AppLayout), sodass ein Klick auf eine andere Seite den Fehler aufloest.
 *
 * Error Boundaries muessen Klassenkomponenten sein (componentDidCatch /
 * getDerivedStateFromError gibt es nur dort).
 */
export class RouteErrorBoundary extends React.Component<
  RouteErrorBoundaryProps,
  RouteErrorBoundaryState
> {
  state: RouteErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): RouteErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // In die Browser-Konsole, damit der echte Stacktrace fuer die Diagnose
    // sichtbar bleibt (die App hat sonst kein zentrales Fehler-Logging).
    // eslint-disable-next-line no-console
    console.error('Seitenfehler abgefangen:', error, info.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="page-container" role="alert">
        <div className="card" style={{ maxWidth: 640, margin: '2rem auto', textAlign: 'center' }}>
          <Icon name="alert" size={32} />
          <h2 className="card-title" style={{ marginTop: '0.5rem' }}>
            Diese Seite konnte nicht geladen werden
          </h2>
          <p className="text-muted">
            Ein unerwarteter Fehler hat das Anzeigen der Seite verhindert. Die
            übrigen Bereiche funktionieren weiter.
          </p>
          <pre
            style={{
              textAlign: 'left',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              background: 'var(--surface-muted, #f5f5f5)',
              padding: '0.75rem',
              borderRadius: 6,
              fontSize: '0.8rem',
              marginTop: '1rem',
            }}
          >
            {error.message || String(error)}
          </pre>
          <div className="btn-group" style={{ justifyContent: 'center', marginTop: '1rem' }}>
            <button type="button" className="btn btn-primary" onClick={() => window.location.reload()}>
              <Icon name="refresh" size={14} /> Neu laden
            </button>
            <Link to="/" className="btn btn-ghost">
              Zur Startseite
            </Link>
          </div>
        </div>
      </div>
    );
  }
}

export default RouteErrorBoundary;
